/* eslint-disable no-unused-vars */
/* eslint-disable react/no-unknown-property */
import {
  Dispatch,
  forwardRef,
  MouseEvent,
  SetStateAction,
  useEffect,
} from "react";
import { Source } from "@types";
import Controls from "./controls";
import classNames from "classnames";
import useManageVideo from "./hooks/useMangeVideo";
import Image from "next/image";
import useOrientation from "@shared/hooks/useOrientation";
import { useMediaQuery } from "@shared/hooks/useMediaQuery";
import { twMerge } from "tailwind-merge";
import { getVideoType } from "@utils/downloadLink";
import { isInDevEnv } from "@utils/helpers";
import RightClickMenu from "./right-click-menu";

export type Props = {
  sources: Source[];
  showTheater?: boolean;
  poster: string;
  theaterMode: boolean;
  timeUpdateHandler?: (
    time: number,
    duration: number,
    seek: boolean
  ) => Promise<void>;
  onEnd?: (time: number, duration: number) => Promise<void>;

  autoPlayOnSrcChange: boolean;
  className?: ((isMobileView?: boolean) => string) | string;
  id?: number | string;
  nextLessonName?: string;
  subtitleSrc?: string;
  stepId?: number;
  startFrom?: number;
  goNext?: () => void;
  isPreview?: boolean;
  jumpVideoTime?: number | null;
  setJumpVideoTime?: Dispatch<SetStateAction<number | null>>;
  onPlay?: (() => void) | undefined;
};
export type PlayerElement = {
  togglePlayHandler: (
    e?: MouseEvent<HTMLElement | SVGSVGElement> | KeyboardEvent
  ) => void;
  pausePlayer: () => void;
};

export type Cue = {
  start: number;
  end: number;
  text: string;
};

const Player = forwardRef<PlayerElement, Props>(
  (
    {
      sources,
      showTheater,
      theaterMode,
      poster,
      timeUpdateHandler,
      onEnd,
      autoPlayOnSrcChange,
      className = "",
      id,
      nextLessonName,
      subtitleSrc,
      stepId,
      startFrom,
      goNext,
      isPreview,
      jumpVideoTime,
      setJumpVideoTime,
      onPlay,
    },
    ref
  ) => {
    const { isPlaying, menuRef, togglePlayByClickHandler, ...videoProps } =
      useManageVideo({
        ref,
        sources,
        nextLessonName,
        theaterMode,
        subtitle: subtitleSrc,
        startFrom,
        curVideoId: id,
        timeUpdateHandler,
        onEnd,
        autoPlayOnSrcChange,
        stepId,
        onPlay,
      });
    const {
      videoContainerRef,
      animatePlay,
      showControls,
      hideControls,
      mouseMoveHandler,
      videoRef,
      settings,
      browser,
      playHandler,
      pauseHandler,
      loadMetaDataHandler,
      videoTimeUpdateHandler,
      videoVolumeChangeHandler,
      replayAfterSeek,
      videoEndedHandler,
      canPlayHandler,
      setIsLoading,
      controlsRef,
      showVideoControls,
      videoSources,
      handleClick,
      isMobile,
      isIPhone,
      jumpToSpeceficTime,
      calcHeight,
      currentText,
      position,
    } = videoProps;
    const orientation = useOrientation();
    const isPortraitMobile =
      useMediaQuery("(max-width:576px)") && orientation === "portrait";

    const isLandscapeMobile =
      useMediaQuery("(min-width:576px) and (max-width:992px)") &&
      orientation === "landscape";

    const isMobileView = isLandscapeMobile || isPortraitMobile;

    const wrapperClassNames =
      typeof className === "string" ? className : className(isMobileView);

    useEffect(() => {
      if (typeof jumpVideoTime === "number" && jumpVideoTime !== null) {
        jumpToSpeceficTime(jumpVideoTime);
        setJumpVideoTime?.(null);
      }
    }, [jumpVideoTime]);

    const fontSize = settings.fullScreen
      ? isMobile
        ? "14px"
        : "20px"
      : isMobile
        ? "12px"
        : "18px";

    const goToNextLessonHandler = (autoplay?: boolean) => {
      goNext?.();
      if (autoplay) {
        setTimeout(() => {
          if (!isPlaying) togglePlayByClickHandler();
        }, 800);
      }
    };

    return (
      <>
        <div
          className={twMerge(
            isMobile ? "is-mobile" : "is-desktop",
            isMobileView ? "is-mobile-view" : "",
            "relative flex justify-start items-start aspect-video ",
            wrapperClassNames,
            isPreview
              ? "rounded-lg overflow-hidden border border-fds-divider"
              : "bg-fds-always-black  video-player-sizing"
          )}
        >
          <div className="w-full h-full">
            <div
              ref={videoContainerRef}
              className={classNames(
                "courseItemPageSelector",
                "nplay-video-container w-full h-full rel outline-none focus:outline-none border-none !border-0",
                animatePlay ? "cursor-default" : ""
              )}
              onMouseEnter={showControls}
              onMouseLeave={hideControls}
              onMouseMove={mouseMoveHandler}
            >
              <video
                ref={videoRef}
                className={classNames(
                  "relative nplay-video w-full h-full top-0 outline-none align-middle block object-contain focus:outline-none",
                  settings.fullScreen &&
                    "top-1/2 -translate-y-1/2 sm:translate-y-0 sm:top-0 sm:w-full sm:h-full subtitle",
                  browser.isFirefox ? "firefox" : "noFireFox"
                )}
                preload="metadata"
                fetchPriority="high"
                poster={poster}
                muted={settings.isMuted}
                autoPlay={Boolean(isIPhone) ? false : autoPlayOnSrcChange}
                controls={Boolean(isIPhone) || showVideoControls}
                onClick={handleClick}
                onPlay={playHandler}
                onPause={pauseHandler}
                onLoadedMetadata={loadMetaDataHandler}
                onTimeUpdate={videoTimeUpdateHandler}
                onVolumeChange={videoVolumeChangeHandler}
                onSeeked={replayAfterSeek}
                onEnded={videoEndedHandler}
                onCanPlay={canPlayHandler}
                onWaiting={() => setIsLoading(true)}
                onPlaying={() => setIsLoading(false)}
                key={stepId || id || sources[0].url}
                crossOrigin={isInDevEnv() ? undefined : "anonymous"}
                playsInline={Boolean(isIPhone)}
              >
                {videoSources.map((m) => (
                  <source
                    key={m.label}
                    src={m.url}
                    type={m.type || getVideoType(m.url)}
                  />
                ))}
              </video>
              {currentText && settings.subtitleOn && (
                <span
                  className="customSubtitle"
                  style={{
                    transform: `translate(-50%,-${calcHeight()}px)`,
                    fontSize: fontSize,
                  }}
                >
                  {currentText}
                </span>
              )}

              {!isIPhone && typeof isPlaying === "undefined" && poster ? (
                <div className="absolute inset-0 pointer-events-none">
                  <Image
                    src={poster}
                    fill
                    alt="Poster"
                    fetchPriority="high"
                    loading="eager"
                    className="object-contain"
                  />
                </div>
              ) : null}
              {!isIPhone && (
                <Controls
                  ref={controlsRef}
                  {...videoProps}
                  togglePlayByClickHandler={togglePlayByClickHandler}
                  videoNode={videoRef.current}
                  sources={videoSources}
                  isPlaying={Boolean(isPlaying)}
                  showTheater={showTheater}
                  goNext={goToNextLessonHandler}
                  isControlsVisible={videoProps.isControlsVisible}
                />
              )}
              {position.show && (
                <RightClickMenu
                  left={position.x || 0}
                  top={position.y || 0}
                  isMuted={settings.isMuted}
                  speedRateChangeHandler={videoProps.speedRateChangeHandler}
                  toggleMuteHandler={videoProps.toggleMuteHandler}
                  togglePlayByClickHandler={togglePlayByClickHandler}
                  togglePipHandler={videoProps.togglePipHandler}
                  pipEnable={settings.pipEnable}
                  fullScreenEnable={settings.fullScreenEnable}
                  toggleFullscreenHandler={videoProps.toggleFullscreenHandler}
                  targetRef={menuRef}
                  startPlayer={videoProps.startPlayer}
                  currentSpeed={settings.speedRate}
                  isPlaying={isPlaying || false}
                  closeMenu={videoProps.closeMenu}
                />
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
);

Player.displayName = "Player";

export default Player;
